'use client';
import React, { useState, useEffect, useRef } from 'react';

export default function QRGeneratorTool() {
  const [text, setText] = useState('https://msupertools.app');
  const [size, setSize] = useState(256);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const generateQR = async (value: string, qrSize: number) => {
    if (!value.trim()) { setQrDataUrl(null); return; }
    setGenerating(true);
    try {
      const QRCode = (await import('qrcode')).default;
      const canvas = canvasRef.current;
      if (!canvas) return;
      await QRCode.toCanvas(canvas, value, {
        width: qrSize,
        margin: 2,
        color: { dark: '#0F172A', light: '#FFFFFF' },
      });
      setQrDataUrl(canvas.toDataURL('image/png'));
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => generateQR(text, size), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [text, size]);

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = 'qrcode.png';
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Input */}
      <div>
        <label className="block font-semibold text-text-primary text-sm mb-2">Text or URL</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Enter text or URL to encode..."
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue resize-none"
        />
        <p className="text-text-muted text-xs mt-1">{text.length} characters</p>
      </div>

      {/* Size */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-semibold text-text-primary text-sm">Size</label>
          <span className="text-brand-blue font-bold text-sm">{size}px</span>
        </div>
        <input
          type="range"
          min={128}
          max={512}
          step={64}
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{ accentColor: '#2563EB' }}
        />
      </div>

      {/* QR Preview */}
      <div className="flex flex-col items-center gap-4">
        <canvas ref={canvasRef} className="rounded-xl border border-gray-100 hidden" />
        {qrDataUrl ? (
          <div className="bg-white rounded-2xl p-4 border border-gray-100 inline-block">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={qrDataUrl} alt="Generated QR Code" className="rounded-xl" style={{ width: Math.min(size, 280), height: Math.min(size, 280) }} />
          </div>
        ) : (
          <div className="w-48 h-48 bg-surface rounded-2xl border border-dashed border-gray-200 flex items-center justify-center">
            <p className="text-text-muted text-sm">QR code preview</p>
          </div>
        )}
        {generating && <p className="text-text-muted text-sm animate-pulse">Generating…</p>}
      </div>

      {/* Download */}
      <button
        onClick={handleDownload}
        disabled={!qrDataUrl}
        className="btn-primary w-full text-base py-3.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        Download QR Code
      </button>
    </div>
  );
}
